import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Typography, Button, Table, Tag, Space, Input, Modal,
    Tooltip, Popconfirm, Row, Col, Popover,
    Avatar, Empty,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import {
    PlusOutlined, EditOutlined, CopyOutlined, DeleteOutlined, SearchOutlined,
    HistoryOutlined, ExclamationCircleOutlined,
    ReloadOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { profilsService } from '../../services/ProfileService';
import { ACTION_COLORS } from '../../variables/rbaColors';
import HistoriqueProfilModal from './HistoriqueProfilModal';
import PageHeader from '../../components/PageHeader';

const { Text } = Typography;

const groupByModule = (habilitations) =>
    habilitations.reduce((acc, h) => {
        const mod = h.moduleNom || h.moduleEntity?.nomModule || i18n.t('profils:liste.autresModules');
        if (!acc[mod]) acc[mod] = [];
        acc[mod].push(h);
        return acc;
    }, {});

const HabPopoverContent = ({ habs }) => {
    const { t } = useTranslation('profils');
    if (!habs?.length) return <Text type="secondary">{t('liste.aucuneHabilitation')}</Text>;
    const grouped = groupByModule(habs);
    return (
        <div style={{ maxWidth: 360, maxHeight: 320, overflowY: 'auto' }}>
            {Object.entries(grouped).map(([mod, items]) => (
                <div key={mod} style={{ marginBottom: 10 }}>
                    <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {mod}
                    </Text>
                    <div style={{ marginTop: 4 }}>
                        <Space size={[4, 4]} wrap>
                            {items.map((h) => (
                                <Tag
                                    key={h.habilitationTrackingId}
                                    color={ACTION_COLORS[h.action]}
                                    style={{ fontSize: 11, margin: 0 }}
                                >
                                    {h.codeHabilitation}
                                </Tag>
                            ))}
                        </Space>
                    </div>
                </div>
            ))}
        </div>
    );
};

// Composant principal
export default function Profils() {
    const { t } = useTranslation('profils');
    const navigate = useNavigate();

    const [profils, setProfils] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    // Fenêtre historique
    const [histOpen, setHistOpen] = useState(false);
    const [histProfil, setHistProfil] = useState(null);

    // ── Chargement ────────────────────────────────────────────────────────────

    const loadProfils = useCallback(async () => {
        setLoading(true);
        try {
            const res = await profilsService.list();
            setProfils(res.data?.data || res.data || []);
        } catch {
            toast.error(i18n.t('profils:liste.erreurChargement'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadProfils();
    }, [loadProfils]);

    // ── Filtrage ──────────────────────────────────────────────────────────────

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        if (!q) return profils;
        return profils.filter(
            (p) =>
                p.profilNom?.toLowerCase().includes(q) ||
                p.commentaire?.toLowerCase().includes(q),
        );
    }, [profils, search]);

    // ── Suppression ───────────────────────────────────────────────────────────

    const handleDelete = async (record) => {
        try {
            await profilsService.delete(record.profilTrackingId);
            toast.success(t('liste.supprime'));
            loadProfils();
        } catch (err) {
            toast.error(err?.response?.data?.message || t('liste.erreurSuppression'));
        }
    };

    // ── Colonnes ──────────────────────────────────────────────────────────────

    const columns = [
        {
            title: t('commun.nomProfil'),
            dataIndex: 'profilNom',
            sorter: (a, b) => a.profilNom.localeCompare(b.profilNom),
            render: (v) => (
                <Space>
                    <Avatar
                        size={28}
                        icon={<SafetyCertificateOutlined />}
                        style={{ background: '#e8f5ed', color: '#1a6b3c', flexShrink: 0 }}
                    />
                    <Text strong style={{ fontSize: 13 }}>{v}</Text>
                </Space>
            ),
        },
        {
            title: t('commun.commentaire'),
            dataIndex: 'commentaire',
            ellipsis: { showTitle: false },
            render: (v) => (
                <Tooltip title={v} placement="topLeft">
                    <Text type="secondary" style={{ fontSize: 13 }}>{v || '-'}</Text>
                </Tooltip>
            ),
        },
        {
            title: t('liste.colonnes.habilitations'),
            dataIndex: 'habilitations',
            width: 160,
            render: (habs) => {
                const count = habs?.length || 0;
                return (
                    <Popover
                        content={<HabPopoverContent habs={habs} />}
                        title={
                            <Space>
                                <SafetyCertificateOutlined style={{ color: '#1a6b3c' }} />
                                <span>{t('commun.nbHabilitations', { count })}</span>
                            </Space>
                        }
                        trigger="click"
                        placement="left"
                    >
                        <Tag
                            color={count > 0 ? 'green' : 'default'}
                            style={{ cursor: 'pointer' }}
                        >
                            {t('commun.nbHabilitations', { count })}
                        </Tag>
                    </Popover>
                );
            },
        },
        {
            title: t('commun.organisme'),
            dataIndex: 'organisme',
            width: 180,
            render: (org) =>
                org?.raisonSociale
                    ? <Tag color="geekblue">{org.raisonSociale}</Tag>
                    : <Tag color="gold">{t('commun.global')}</Tag>,
        },
        {
            title: t('liste.colonnes.actions'),
            key: 'actions',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space size={2}>
                    {/* Dupliquer : on repart des droits d'un profil qui marche plutôt
                        que de recocher deux cents cases une par une. */}
                    <Tooltip title={t('liste.actions.dupliquer', { nom: record.profilNom })}>
                        <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => navigate(`/profils/new?copier=${record.profilTrackingId}`)}
                            style={{ color: '#1a6b3c' }}
                        />
                    </Tooltip>

                    {/* ← navigate vers la page d'édition dédiée */}
                    <Tooltip title={t('liste.actions.modifier')}>
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/profils/${record.profilTrackingId}/edit`)}
                            style={{ color: '#1a6b3c' }}
                        />
                    </Tooltip>

                    <Tooltip title={t('liste.actions.historique')}>
                        <Button
                            type="text"
                            size="small"
                            icon={<HistoryOutlined />}
                            onClick={() => { setHistProfil(record); setHistOpen(true); }}
                            style={{ color: '#6b7280' }}
                        />
                    </Tooltip>

                    <Popconfirm
                        title={t('liste.suppression.titre')}
                        description={t('liste.suppression.description')}
                        icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                        onConfirm={() => handleDelete(record)}
                        okText={t('liste.suppression.confirmer')}
                        okButtonProps={{ danger: true }}
                        cancelText={t('commun.annuler')}
                    >
                        <Tooltip title={t('liste.actions.supprimer')}>
                            <Button type="text" size="small" icon={<DeleteOutlined />} danger />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Layout title={t('liste.titre')}>
            <div style={{ padding: '24px', background: '#f9fafb', minHeight: '100vh' }}>

                {/* ── Header ── */}
                {/* Le compte vient de `filtered` et non de la liste brute : la
                    recherche est purement cliente ici, donc `filtered.length` est
                    le seul nombre qui corresponde a ce que le tableau affiche. */}
                <PageHeader
                    titre={t('liste.titre')}
                    icone={<SafetyCertificateOutlined />}
                    compte={filtered.length}
                    unite={t('liste.unite')}
                    actions={(
                        /* ← navigate vers la page de création dédiée */
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => navigate('/profils/new')}
                            style={{ background: '#1a6b3c' }}
                        >
                            {t('liste.ajouter')}
                        </Button>
                    )}
                />

                {/* ── Barre de recherche ── */}
                <div style={{
                    background: '#fff', border: '1px solid #e5e7eb',
                    borderRadius: 8, padding: '14px 20px', marginBottom: 16,
                }}>
                    <Row gutter={[12, 12]} align="middle">
                        <Col xs={24} sm={20} md={14}>
                            <Input
                                prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
                                placeholder={t('liste.rechercher')}
                                allowClear
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </Col>
                        <Col xs={24} sm={4} md={10} style={{ textAlign: 'right' }}>
                            <Space>
                                <Tooltip title={t('liste.reinitialiser')}>
                                    <Button
                                        icon={<ReloadOutlined />}
                                        onClick={() => setSearch('')}
                                        size="small"
                                    />
                                </Tooltip>
                            </Space>
                        </Col>
                    </Row>
                </div>

                {/* ── Tableau ── */}
                <div style={{
                    background: '#fff', borderRadius: 8,
                    border: '1px solid #e5e7eb', overflow: 'hidden',
                }}>
                    <Table
                        dataSource={filtered}
                        columns={columns}
                        rowKey="profilTrackingId"
                        loading={loading}
                        size="middle"
                        scroll={{ x: 800 }}
                        pagination={{
                            pageSize: 20,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50'],
                            showTotal: (total) => t('liste.total', { count: total }),
                        }}
                        locale={{
                            emptyText: (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={search ? t('liste.aucunCorrespondant') : t('liste.aucunCree')}
                                />
                            ),
                        }}
                    />
                </div>

                {/* ── Fenêtre Historique ── */}
                {/* Le profil reste posé à la fermeture : la fenêtre garde son titre
                    pendant l'animation, le suivant le remplace à l'ouverture. */}
                <HistoriqueProfilModal
                    open={histOpen}
                    onClose={() => setHistOpen(false)}
                    profil={histProfil}
                />
            </div>
        </Layout>
    );
}